const LEGACY_HOST = 'pub-91424f27b06a407bb95bcf26cd4dbde3.r2.dev';
const LEGACY_WORKER_HOST = 'time4subs-uploads.ryan-b00.workers.dev';
const MEDIA_WORKER_HOST = 'time4subs-media.ryan-b00.workers.dev';
const GATEWAY = `https://${MEDIA_WORKER_HOST}/`;
const BLOB_TTL_MS = 5 * 60 * 1000;
const SIGNED_TTL_MS = 45 * 60 * 1000;
const FAILED_TTL_MS = 30 * 1000;
const MAX_ENTRIES = 64;
const MAX_BLOB_BYTES = 64 * 1024 * 1024;
const MAX_ITEM_BYTES = 25 * 1024 * 1024;
const MAX_CONCURRENT = 4;

export function classifyMedia(value) {
  if (typeof value !== 'string' || !value.trim()) return { kind: 'invalid' };
  const input = value.trim();
  let key = input;
  let fromLegacyHost = false;
  if (/^https?:\/\//i.test(input)) {
    let url;
    try { url = new URL(input); } catch { return { kind: 'invalid' }; }
    const host = url.hostname.toLowerCase();
    if (url.protocol !== 'https:' || ![LEGACY_HOST, LEGACY_WORKER_HOST, MEDIA_WORKER_HOST].includes(host)) {
      return url.protocol === 'https:' ? { kind: 'external', url: input } : { kind: 'invalid' };
    }
    fromLegacyHost = true;
    try {
      const rawPath = decodeURIComponent(input.replace(/^https?:\/\/[^/]+/i, '').split(/[?#]/)[0]);
      if (rawPath.split('/').some(part => part === '.' || part === '..')) return { kind: 'invalid' };
      const queryKeys = host === LEGACY_WORKER_HOST || host === MEDIA_WORKER_HOST
        ? url.searchParams.getAll('key') : [];
      if (queryKeys.length > 1) return { kind: 'invalid' };
      key = queryKeys.length === 1 ? queryKeys[0] : decodeURIComponent(url.pathname.replace(/^\/+/, ''));
    }
    catch { return { kind: 'invalid' }; }
  } else if (/^[a-z][a-z\d+.-]*:/i.test(input) || input.startsWith('//')) {
    return { kind: 'invalid' };
  }

  if (!key || key.length > 2048 || key.startsWith('/') || key.includes('\\')
      || key.includes('\0') || key.includes('?') || key.includes('#')
      || key.split('/').some(part => !part || part === '.' || part === '..')
      || /%(?:2e|2f|5c|00)/i.test(key)) return { kind: 'invalid' };
  if (key.includes('/projects/') || key.startsWith('projects/')) {
    // The distinct PM4Subs storage path keeps its existing presigned endpoint.
    return { kind: fromLegacyHost ? 'private' : 'projects', key };
  }
  return { kind: 'private', key };
}

export function createPrivateMediaClient({ getSession, fetchImpl, createObjectURL, revokeObjectURL,
  now = () => Date.now(), pmApiUrl = '' }) {
  let ownerId = null;
  let generation = 0;
  let blobBytes = 0;
  let activeTransfers = 0;
  let transferGeneration = 0;
  const cache = new Map();
  const failures = new Map();
  const inFlight = new Map();
  const queuedTransfers = [];

  function finishTransfer(transfer) {
    if (transfer.generation === transferGeneration) activeTransfers -= 1;
    drainTransfers();
  }

  function drainTransfers() {
    while (activeTransfers < MAX_CONCURRENT && queuedTransfers.length) {
      const transfer = queuedTransfers.shift();
      if (transfer.signal.aborted || transfer.generation !== transferGeneration) {
        transfer.resolve(null);
        continue;
      }
      activeTransfers += 1;
      Promise.resolve().then(transfer.work).then(transfer.resolve, () => transfer.resolve(null)).finally(finishTransfer.bind(null, transfer));
    }
  }

  function runBounded(work, signal) {
    return new Promise(resolve => {
      queuedTransfers.push({ work, signal, resolve, generation: transferGeneration });
      drainTransfers();
    });
  }

  function drop(key) {
    const entry = cache.get(key);
    if (!entry) return;
    cache.delete(key);
    clearTimeout(entry.timer);
    if (entry.kind === 'private') {
      blobBytes -= entry.bytes;
      revokeObjectURL(entry.url);
    }
  }

  function invalidate() {
    generation += 1;
    ownerId = null;
    for (const request of inFlight.values()) request.controller.abort();
    inFlight.clear();
    transferGeneration += 1;
    activeTransfers = 0;
    for (const transfer of queuedTransfers.splice(0)) transfer.resolve(null);
    for (const key of cache.keys()) drop(key);
    failures.clear();
  }

  function setSession(session) {
    const nextOwner = session?.user?.id || null;
    if (nextOwner !== ownerId) {
      invalidate();
      ownerId = nextOwner;
    }
  }

  function cached(key) {
    const entry = cache.get(key);
    if (!entry) return null;
    if (entry.owner !== ownerId || entry.generation !== generation || now() - entry.at >= entry.ttl) {
      drop(key);
      return null;
    }
    cache.delete(key);
    cache.set(key, entry);
    return entry.url;
  }

  function rememberFailure(key) {
    failures.delete(key);
    failures.set(key, now());
    while (failures.size > MAX_ENTRIES) failures.delete(failures.keys().next().value);
  }

  function getPhotoUrl(value) {
    if (!ownerId) return null;
    const item = classifyMedia(value);
    if (item.kind === 'external') return item.url;
    if (item.kind === 'invalid') return null;
    return cached(`${item.kind}:${item.key}`);
  }

  function store(key, entry) {
    drop(key);
    cache.set(key, entry);
    entry.timer = setTimeout(() => { if (cache.get(key) === entry) drop(key); }, entry.ttl);
    entry.timer.unref?.();
    if (entry.kind === 'private') blobBytes += entry.bytes;
    while (cache.size > MAX_ENTRIES || blobBytes > MAX_BLOB_BYTES) drop(cache.keys().next().value);
  }

  async function getPresignedPhotoUrl(value) {
    // Only AuthContext may open a media owner boundary. A still-stored SDK
    // session during remote sign-out must never bootstrap it again.
    if (!ownerId) return null;
    const item = classifyMedia(value);
    if (item.kind === 'external') return item.url;
    if (item.kind === 'invalid') return null;
    const key = `${item.kind}:${item.key}`;
    const hit = getPhotoUrl(value);
    if (hit) return hit;
    if (failures.has(key)) {
      if (now() - failures.get(key) < FAILED_TTL_MS) return null;
      failures.delete(key);
    }
    const existing = inFlight.get(key);
    if (existing && existing.generation === generation) return existing.promise;

    const startedGeneration = generation;
    const startedOwner = ownerId;
    const controller = new AbortController();
    const promise = (async () => {
      try {
        const { data, error } = await getSession();
        if (error || generation !== startedGeneration) return null;
        const session = data?.session;
        if (!session?.user?.id || !session.access_token || session.user.id !== startedOwner) {
          invalidate();
          return null;
        }
        const requestGeneration = generation;
        const requestOwner = ownerId;
        if (session.user.id !== requestOwner || controller.signal.aborted) return null;

        if (item.kind === 'projects') {
          if (!pmApiUrl) return null;
          const payload = await runBounded(async () => {
            const response = await fetchImpl(`${pmApiUrl.replace(/\/$/, '')}/api/r2-download-url`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
              body: JSON.stringify({ filePath: item.key }), credentials: 'omit', redirect: 'error', signal: controller.signal,
            });
            return response.ok ? response.json() : null;
          }, controller.signal);
          if (!payload || generation !== requestGeneration || ownerId !== requestOwner) return null;
          const downloadUrl = payload?.downloadUrl;
          if (typeof downloadUrl !== 'string' || !/^https:\/\//i.test(downloadUrl)) return null;
          store(key, { kind: 'projects', owner: requestOwner, generation: requestGeneration,
            url: downloadUrl, at: now(), ttl: SIGNED_TTL_MS });
          return downloadUrl;
        }

        const blob = await runBounded(async () => {
          const response = await fetchImpl(`${GATEWAY}?key=${encodeURIComponent(item.key)}`, {
            method: 'GET', headers: { Authorization: `Bearer ${session.access_token}` },
            credentials: 'omit', redirect: 'error', signal: controller.signal,
          });
          const declaredSize = Number(response.headers?.get?.('content-length'));
          if (!response.ok || declaredSize > MAX_ITEM_BYTES) return null;
          return response.blob();
        }, controller.signal);
        if (!blob || generation !== requestGeneration || ownerId !== requestOwner || controller.signal.aborted) return null;
        if (!blob.size || blob.size > MAX_ITEM_BYTES) return null;
        const url = createObjectURL(blob);
        if (generation !== requestGeneration || ownerId !== requestOwner) {
          revokeObjectURL(url);
          return null;
        }
        store(key, { kind: 'private', owner: requestOwner, generation: requestGeneration,
          url, bytes: blob.size, at: now(), ttl: BLOB_TTL_MS });
        return cache.has(key) ? url : null;
      } catch {
        if (!controller.signal.aborted && generation === startedGeneration) rememberFailure(key);
        return null;
      } finally {
        if (inFlight.get(key)?.promise === promise) inFlight.delete(key);
      }
    })();
    inFlight.set(key, { generation: startedGeneration, controller, promise });
    return promise;
  }

  return { getPhotoUrl, getPresignedPhotoUrl, setSession, invalidate, classifyMedia };
}
